import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Clock, Circle, MapPin, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { WeeklyHours, DAYS_OF_WEEK, DAY_NAMES, getCurrentDayKey, isBusinessOpen, formatTime } from '@communitly/shared';
import { useTimeFormatParameters } from '@/hooks/useParameters';
import { Button } from './ui/button';

interface BusinessInfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  openingHours: WeeklyHours | null;
  businessName: string;
  address?: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    county?: string | null;
    postalCode?: string | null;
    country?: string | null;
  };
  website?: string;
}

const BusinessInfoModal: React.FC<BusinessInfoModalProps> = ({
  open,
  onOpenChange,
  openingHours,
  businessName,
  address,
  website
}) => {
  const { t } = useTranslation();
  const { timeDisplayFormat } = useTimeFormatParameters();
  const currentDay = getCurrentDayKey();
  const openingStatus = isBusinessOpen(openingHours, timeDisplayFormat, t);

  const formatAddress = () => {
    if (!address) return '';
    
    const parts = [
      address.line1,
      address.line2,
      address.city,
      address.county,
      address.postalCode,
      address.country
    ].filter(Boolean);
    
    return parts.join(', ');
  };

  const formatWebsiteDisplay = (url: string) => {
    return url.replace(/^https?:\/\/(www\.)?/i, '');
  };

  const OpeningHoursDisplay = () => {
    if (!openingHours) {
      return (
        <div className="text-center py-8">
          <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">{t('service.details.opening_status.hours_unavailable')}</p>
        </div>
      );
    }

    return (
      <div className="space-y-1.5">
        {DAYS_OF_WEEK.map(day => {
          const dayHours = openingHours[day];
          const isToday = day === currentDay;
          
          if (!dayHours) return null;

          const getTodayRowStyling = () => {
            if (!isToday) return '';
            
            if (!openingStatus.isOpen) {
              return 'bg-gray-100 border border-gray-200';
            } else {
              return 'bg-green-50 border border-green-200';
            }
          };

          const getTodayTextStyling = () => {
            if (!isToday) return 'text-gray-900';
            
            if (!openingStatus.isOpen) {
              return 'font-medium text-gray-900';
            } else {
              return 'font-medium text-green-900';
            }
          };

          const getTodayTimeStyling = () => {
            if (!isToday) return 'text-muted-foreground';
            
            if (!openingStatus.isOpen) {
              return 'font-medium text-muted-foreground';
            } else {
              return 'font-medium text-green-900';
            }
          };

          return (
            <div 
              key={day}
              className={cn(
                "flex items-center justify-between py-1.5 px-2 rounded-lg transition-colors",
                getTodayRowStyling()
              )}
            >
              <span className={cn(
                "text-sm min-w-[80px]",
                getTodayTextStyling()
              )}>
                {t(DAY_NAMES[day])}
              </span>
              
              <div className="flex items-center gap-2">
                {dayHours.closed ? (
                  <span className="text-sm text-muted-foreground">
                    {t('service.details.day_names.closed')}
                  </span>
                ) : (
                  <span className={cn(
                    "text-sm font-mono",
                    getTodayTimeStyling()
                  )}>
                    {formatTime(dayHours.open, timeDisplayFormat)} - {formatTime(dayHours.close, timeDisplayFormat)}
                  </span>
                )}
                
                <Circle 
                  className={cn(
                    "w-2 h-2 fill-current ml-1",
                    dayHours.closed ? "text-gray-400" : "text-green-500"
                  )} 
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md mx-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Clock className="h-5 w-5 text-gray-600" />
            {t('service.details.business_info')}
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-2">
            {businessName}
          </p>
        </DialogHeader>
        
        <div className="mt-4 space-y-5">
          {/* Address Section */}
          {address && Object.values(address).some(val => val) && (
            <div className="space-y-2">
              <h3 className="flex items-center gap-2 text-sm font-medium text-gray-900">
                <MapPin className="h-4 w-4 text-gray-600" />
                {t('service.details.address')}
              </h3>
              <p className="text-sm text-muted-foreground pl-6">
                {formatAddress()}
              </p>
            </div>
          )}

          {/* Contact Section - Only Website */}
          {website && (
            <div className="space-y-2">
              <h3 className="flex items-center gap-2 text-sm font-medium text-gray-900">
                <Globe className="h-4 w-4 text-gray-600" />
                {t('service.details.contact')}
              </h3>
              <div className="pl-6">
                <a 
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline flex items-center gap-2"
                >
                  <Globe className="h-3 w-3" />
                  {formatWebsiteDisplay(website)}
                </a>
              </div>
            </div>
          )}

          {/* Opening Hours Section */}
          <div className="space-y-2">
            <h3 className="flex items-center gap-2 text-sm font-medium text-gray-900">
              <Clock className="h-4 w-4 text-gray-600" />
              {t('service.details.opening_hours')}
            </h3>
            <div className="pl-6">
              <OpeningHoursDisplay />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BusinessInfoModal;